/**
 * `/admin` → Transcripts → **Usage**: how the assistant is used, in numbers.
 *
 * Counts only - no question text - so, like the list of people, looking at it
 * is not recorded in Activity (`transcripts.usage_stats`). Root only, as the
 * rest of Transcripts.
 *
 * Three things, top to bottom: the totals for the period, questions per day,
 * and a row per person. "Disliked" is an answer marked "Not what I asked for";
 * its share is of that person's questions, which is the number worth comparing.
 * A row opens that person's chats.
 *
 * Imported chats (carried over from browsers) are left out by the server: they
 * have no model and the browser's dates.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { getUsageStats } from '../../api/endpoints.ts';
import type { UsageStats } from '../../api/types.ts';
import { timeAgo } from '../../utils/dates.ts';
import { userMessage } from '../../api/errors.ts';

const PERIODS: Array<{ days: number; label: string }> = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 0, label: 'All' },
];

const PERIOD_KEY = 'neptune_admin_usage_days';

function readPeriod(): number {
  try {
    const stored = Number(localStorage.getItem(PERIOD_KEY));
    return PERIODS.some((p) => p.days === stored) && localStorage.getItem(PERIOD_KEY) !== null ? stored : 30;
  } catch {
    return 30;
  }
}

function cost(usd: number): string {
  return usd < 0.01 && usd > 0 ? '<$0.01' : `$${usd.toFixed(2)}`;
}

function share(part: number, whole: number): string {
  if (!whole || !part) return '';
  const pct = (part / whole) * 100;
  return pct < 1 ? '<1%' : `${Math.round(pct)}%`;
}

function isoDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function dayLabel(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

/** Every day of the period, zeros included - a quiet day is data too. */
export function fillDays<T extends { day: string }>(
  perDay: T[],
  days: number | null,
  zero: (day: string) => T,
  today = new Date(),
): T[] {
  const byDay = new Map(perDay.map((d) => [d.day, d]));
  const end = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
  const start = days
    ? new Date(end.getTime() - (days - 1) * 86_400_000)
    : perDay.length ? new Date(`${perDay[0]!.day}T00:00:00Z`) : end;
  const out: T[] = [];
  for (let t = start.getTime(); t <= end.getTime(); t += 86_400_000) {
    const day = isoDay(new Date(t));
    out.push(byDay.get(day) ?? zero(day));
  }
  return out;
}

@customElement('transcripts-usage')
export class TranscriptsUsage extends LightElement {
  @state() private days = readPeriod();
  @state() private stats: UsageStats | null = null;
  @state() private loading = false;
  @state() private error = '';
  /** The day under the pointer, read out above the bars. */
  @state() private hoverDay: string | null = null;
  private rev = 0;

  override connectedCallback(): void {
    super.connectedCallback();
    void this.load();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.rev++;
  }

  private async load(): Promise<void> {
    const rev = ++this.rev;
    this.loading = true;
    this.error = '';
    try {
      const stats = await getUsageStats(this.days);
      if (rev === this.rev) this.stats = stats;
    } catch (e) {
      if (rev === this.rev) this.error = userMessage(e);
    } finally {
      if (rev === this.rev) this.loading = false;
    }
  }

  private setPeriod(days: number): void {
    if (days === this.days) return;
    this.days = days;
    try {
      localStorage.setItem(PERIOD_KEY, String(days));
    } catch {
      /* the choice still holds for this visit */
    }
    void this.load();
  }

  private openPerson(person: UsageStats['people'][number]): void {
    this.dispatchEvent(new CustomEvent('open-person', {
      detail: { id: person.id, display_name: person.display_name, email: person.email },
    }));
  }

  private renderTiles(t: UsageStats['totals']): TemplateResult {
    const tile = (label: string, value: string, note = '', tone = '') => html`<div class="usage-tile ${tone}">
      <span class="usage-tile-label">${label}</span>
      <span class="usage-tile-value">${value}</span>
      ${note ? html`<span class="usage-tile-note">${note}</span>` : nothing}
    </div>`;
    return html`<div class="usage-tiles">
      ${tile('Questions', String(t.questions), `${t.chats} chat${t.chats === 1 ? '' : 's'}`)}
      ${tile('People', String(t.people))}
      ${tile('Not what they asked', String(t.disliked), share(t.disliked, t.questions) ? `${share(t.disliked, t.questions)} of questions` : '', t.disliked ? 'is-disliked' : '')}
      ${tile('Failed', String(t.failed), share(t.failed, t.questions) ? `${share(t.failed, t.questions)} of questions` : '', t.failed ? 'is-error' : '')}
      ${tile('Charts proposed', String(t.plots), t.exports ? `${t.exports} export${t.exports === 1 ? '' : 's'}` : '')}
      ${tile('Median answer time', t.median_latency_ms == null ? '-' : `${(t.median_latency_ms / 1000).toFixed(1)} s`)}
      ${tile('Cost', cost(t.cost_usd))}
    </div>`;
  }

  private renderDays(stats: UsageStats): TemplateResult {
    const days = fillDays(stats.per_day, stats.days, (day) => ({ day, questions: 0, disliked: 0 }));
    const max = Math.max(1, ...days.map((d) => d.questions));
    const hovered = days.find((d) => d.day === this.hoverDay);
    const readout = hovered
      ? `${dayLabel(hovered.day)}: ${hovered.questions} question${hovered.questions === 1 ? '' : 's'}${
          hovered.disliked ? `, ${hovered.disliked} disliked` : ''}`
      : 'Questions per day';
    return html`<section class="usage-section" aria-label="Questions per day">
      <div class="usage-days-head">
        <span class="usage-readout" aria-live="polite">${readout}</span>
        <span class="usage-days-max">${max}</span>
      </div>
      <div class="usage-bars" @mouseleave=${() => { this.hoverDay = null; }}>
        ${days.map((d) => html`<div class="usage-bar-slot ${d.day === this.hoverDay ? 'is-hover' : ''}"
            title=${`${dayLabel(d.day)}: ${d.questions} question${d.questions === 1 ? '' : 's'}${d.disliked ? `, ${d.disliked} disliked` : ''}`}
            @mouseenter=${() => { this.hoverDay = d.day; }}>
            ${d.questions
              ? html`<div class="usage-bar" style=${`height:${(d.questions / max) * 100}%`}></div>`
              : nothing}
          </div>`)}
      </div>
      <div class="usage-days-axis">
        <span>${days.length ? dayLabel(days[0]!.day) : ''}</span>
        <span>${days.length ? dayLabel(days[days.length - 1]!.day) : ''}</span>
      </div>
    </section>`;
  }

  private renderPeople(stats: UsageStats): TemplateResult {
    if (!stats.people.length) return html`<p class="transcripts-note">Nobody asked anything in this period.</p>`;
    return html`<div class="people-table-wrap usage-people"><table class="people-table">
      <thead><tr>
        <th>Person</th>
        <th class="num">Questions</th>
        <th class="num">Chats</th>
        <th class="num" title='Answers marked "Not what I asked for"'>Disliked</th>
        <th class="num">Failed</th>
        <th class="num">Charts</th>
        <th class="num">Cost</th>
        <th>Last asked</th>
      </tr></thead>
      <tbody>
        ${stats.people.map((p) => html`<tr class="usage-row" tabindex="0" title="Read their chats"
            @click=${() => this.openPerson(p)}
            @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter') this.openPerson(p); }}>
          <td><span class="usage-person">${p.display_name}</span>${p.email ? html`<span class="people-muted">${p.email}</span>` : nothing}</td>
          <td class="num">${p.questions}</td>
          <td class="num">${p.chats}</td>
          <td class="num ${p.disliked ? 'usage-disliked' : ''}">${p.disliked || '-'}${
            p.disliked ? html` <span class="people-muted">${share(p.disliked, p.questions)}</span>` : nothing}</td>
          <td class="num">${p.failed || '-'}</td>
          <td class="num">${p.plots || '-'}</td>
          <td class="num">${cost(p.cost_usd)}</td>
          <td><time datetime=${p.last_asked_at}>${timeAgo(p.last_asked_at, 'long')}</time></td>
        </tr>`)}
      </tbody>
    </table></div>`;
  }

  override render(): TemplateResult {
    const stats = this.stats;
    return html`<div class="usage" aria-busy=${this.loading}>
      <div class="usage-head">
        <div class="seg" role="group" aria-label="Period">
          ${PERIODS.map((p) => html`<button type="button" class="seg-btn ${p.days === this.days ? 'active' : ''}"
            aria-pressed=${p.days === this.days} @click=${() => this.setPeriod(p.days)}>${p.label}</button>`)}
        </div>
        <span class="people-muted">Counts only; imported chats left out. Times in UTC.</span>
      </div>
      ${this.error
        ? html`<p class="transcripts-note is-error" role="alert">${this.error}</p>`
        : !stats
          ? html`<p class="transcripts-note">Loading…</p>`
          : html`${this.renderTiles(stats.totals)} ${this.renderDays(stats)} ${this.renderPeople(stats)}`}
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'transcripts-usage': TranscriptsUsage;
  }
}
